export type SymbolFlipStateLike = {
  type: string;
  flipped?: boolean;
  flipHorizontal?: boolean;
  flipVertical?: boolean;
};

export type EffectiveSvgFlipState = {
  flipped: boolean;
  flipHorizontal: boolean;
  flipVertical: boolean;
};

/**
 * SVGs saved before the current coordinate convention do not have `flipped`.
 * The 2D renderer has always treated those symbols as `flipped: true` so old
 * floorplans keep their original appearance.
 */
export function getEffectiveSvgFlipState(
  symbol: Pick<
    SymbolFlipStateLike,
    "flipped" | "flipHorizontal" | "flipVertical"
  >,
): EffectiveSvgFlipState {
  return {
    flipped: symbol.flipped ?? true,
    flipHorizontal: symbol.flipHorizontal ?? false,
    flipVertical: symbol.flipVertical ?? false,
  };
}

/**
 * Returns the plan-space scale used by 3D symbols.
 *
 * Primitive models are authored against the current SVG convention
 * (`flipped: false`). A legacy SVG is vertically mirrored relative to that
 * convention, so its compatibility flip must be composed with any explicit
 * user flip. This makes the front/back orientation in 3D match the unchanged
 * 2D symbol.
 */
export function getSymbol3DPlanScale(
  symbol: SymbolFlipStateLike,
): [number, number, number] {
  const flipX = symbol.flipHorizontal ? -1 : 1;
  const explicitFlipY = symbol.flipVertical ? -1 : 1;
  const legacySvgFlipY =
    symbol.type === "svg" && getEffectiveSvgFlipState(symbol).flipped ? -1 : 1;

  return [flipX, explicitFlipY * legacySvgFlipY, 1];
}
